import { evaluateContextInstance } from '@gonfalon/openapi/operations/evaluateContextInstance';
import { getContextAttributeValues } from '@gonfalon/openapi/operations/getContextAttributeValues';
import { getContextInstances } from '@gonfalon/openapi/operations/getContextInstances';
import { getContextKindByProjectKeyAndKey } from '@gonfalon/openapi/operations/getContextKindByProjectKeyAndKey';
import { getContextKinds } from '@gonfalon/openapi/operations/getContextKinds';
import { getContextKindsByProjectKey } from '@gonfalon/openapi/operations/getContextKindsByProjectKey';
import { getContexts } from '@gonfalon/openapi/operations/getContexts';
import { putContextKind } from '@gonfalon/openapi/operations/putContextKind';
import { searchContexts } from '@gonfalon/openapi/operations/searchContexts';
import { getQueryClient } from '@gonfalon/react-query-client/getQueryClient';
import { createQueryKeys } from '@lukemorales/query-key-factory';

import { createBatchedQueryOptions } from '../internal/createBatchedQueryOptions';
import { createInfiniteQueryOptions } from '../internal/createInfiniteQueryOptions';
import { createMutationHook } from '../internal/createMutationHook';
import { createQueryOptions } from '../internal/createQueryOptions';
import { queryByContextKindKeysTaskRunner } from '../internal/queryByContextKindKeysTaskRunner';
import { queryContextsWithNamesTaskRunner } from '../internal/queryContextsWithNamesTaskRunner';
import { reactQueryResponseAdapter } from '../internal/reactQueryResponseAdapter';
import type { ContextKindsCollectionRep, PostContextsParams } from '../types';

export const useEvaluateContextInstance = createMutationHook(evaluateContextInstance);
export const contextInstancesQuery = createQueryOptions(getContextInstances);
export const contextKindsQuery = createQueryOptions(getContextKindsByProjectKey);
export const contextKindQuery = createQueryOptions(getContextKindByProjectKeyAndKey);

/**
 * Create or update a context kind (upsert via PUT).
 *
 * Owns the cache lifecycle for the context-kinds list: it optimistically writes
 * the change into the `contextKindsQuery` cache (the context-kinds API is
 * eventually consistent and a refetch immediately after the PUT can return stale
 * data), rolls back on error, and marks the query stale once settled. Consumers
 * pass `onSuccess`/`onError` to `mutate` for UI side effects (toasts, closing a
 * modal).
 */
export const useUpdateContextKind = createMutationHook(putContextKind, {
  onMutate: async ({ projectKey, key, body }) => {
    const queryClient = getQueryClient();
    // Match every cached context-kinds list for this project, not just the bare
    // `{ projectKey }` key. Callers read this collection under different query
    // inputs (e.g. `{ query: { expand: 'environmentObservations' } }` on the
    // settings page), which produce distinct cache entries. Writing only the
    // bare key would leave those expanded entries stale, so a newly created kind
    // would never appear in a list that reads the expanded variant.
    const queryKey = contextKindsQuery.partialQueryKey({ projectKey });
    await queryClient.cancelQueries({ queryKey });
    const previous = queryClient.getQueriesData({ queryKey });

    queryClient.setQueriesData<ContextKindsCollectionRep>({ queryKey }, (queryData) => {
      if (!queryData) {
        return queryData;
      }
      const nextItems = [...queryData.items];
      const toUpdateIndex = nextItems.findIndex((item) => item.key === key);
      if (toUpdateIndex !== -1) {
        nextItems[toUpdateIndex] = {
          ...nextItems[toUpdateIndex],
          name: body.name ?? nextItems[toUpdateIndex].name,
          description: body.description ?? nextItems[toUpdateIndex].description,
          archived: body.archived ?? nextItems[toUpdateIndex].archived,
          lastModified: Date.now(),
          version: nextItems[toUpdateIndex].version + 1,
        };
      } else {
        nextItems.push({
          key,
          name: body.name ?? key,
          archived: body.archived ?? false,
          createdFrom: 'custom',
          creationDate: Date.now(),
          lastSeen: undefined,
          lastModified: 0,
          version: 0,
          description: body.description ?? '',
        });
      }
      return { _links: queryData._links, items: nextItems };
    });

    return { previous };
  },
  onError: (_error, _variables, context) => {
    if (context?.previous !== undefined) {
      const queryClient = getQueryClient();
      for (const [queryKey, data] of context.previous) {
        queryClient.setQueryData(queryKey, data);
      }
    }
  },
  // Mark the list stale WITHOUT refetching. The context-kinds API is eventually
  // consistent: an immediate refetch here would race the write and overwrite the
  // optimistic update with stale data (the reason the legacy code polled the GET
  // for up to 60s). `refetchType: 'none'` keeps the optimistic value visible and
  // lets the query reconcile on its next natural trigger (remount / navigation),
  // by which point the server has caught up.
  onSettled: (_data, _error, { projectKey }) => {
    void getQueryClient().invalidateQueries({
      queryKey: contextKindsQuery.partialQueryKey({ projectKey }),
      refetchType: 'none',
    });
  },
});

export const contextKindsForFlagQuery = createQueryOptions(getContextKinds);
export const getContextVersionsQuery = createQueryOptions(getContexts);
export const getContextAttributeValuesQuery = createQueryOptions(getContextAttributeValues);

export const getContextsKindKeysQuery = createBatchedQueryOptions(queryByContextKindKeysTaskRunner);

export const getContextsWithNamesQuery = createBatchedQueryOptions(queryContextsWithNamesTaskRunner);
export const getInfiniteContextsQuery = createInfiniteQueryOptions(searchContexts);
export const getContextsQuery = createQueryOptions(searchContexts);

/**
 * @deprecated Use `createQueryOptions` with the OpenAPI operation directly instead.
 * See packages/rest-api/README.md for the recommended pattern.
 */
export const contexts = createQueryKeys('contexts', {
  list: ({
    projectKey,
    environmentKey,
    params,
  }: {
    projectKey: string;
    environmentKey: string;
    params: PostContextsParams;
  }) => ({
    queryKey: [projectKey, environmentKey, params],
    queryFn: async ({ signal }) =>
      reactQueryResponseAdapter(searchContexts({ projectKey, environmentKey, body: params }, { signal })),
  }),
});
